import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE } from "../../theme/tokens";
import type { BulkNurseJobsAction, Job } from "../../utils/models";
import { type FlatSwitchOption, SystemFlatSwitch } from "../UIToolkit/Clickable/SystemFlatSwitch";
import { InlineHint } from "../UIToolkit/molecules/InlineHint";
import { JobSelector } from "../UIToolkit/molecules/JobSelector";
import type { BulkJobPicks } from "./bulkJobChange";

type Props = {
	picks: BulkJobPicks;
	onPicksChange: (next: BulkJobPicks) => void;
	/** Jobs this department offers — what an employee can be given. */
	enabledJobs: Job[];
	/** Jobs at least one selected employee holds — what can be replaced. */
	heldJobs: Job[];
};

/** Same size on every breakpoint: the switch is the decision here, not a dense header filter. */
const SWITCH_FONT_SIZE = { xs: FONT_SIZE.EMPHASIS, sm: FONT_SIZE.EMPHASIS, lg: FONT_SIZE.EMPHASIS, xl: FONT_SIZE.EMPHASIS };

/** Step 1: add a job, or replace one job with another. */
export const BulkJobChooseStep: FC<Props> = ({ picks, onPicksChange, enabledJobs, heldJobs }) => {
	const { t } = useTranslation();

	const modeOptions = useMemo<FlatSwitchOption<BulkNurseJobsAction>[]>(
		() => [
			{ label: t("team.bulk.jobs.mode.add"), value: "add", "data-testid": "team-bulk-jobs-mode-add" },
			{ label: t("team.bulk.jobs.mode.replace"), value: "replace", "data-testid": "team-bulk-jobs-mode-replace" },
		],
		[t],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, alignItems: "flex-start" }}>
				<SystemFlatSwitch<BulkNurseJobsAction>
					data-testid="team-bulk-jobs-mode"
					options={modeOptions}
					activeIndexOrValue={picks.action}
					onSwitch={(option) => onPicksChange({ ...picks, action: option.value })}
					delayCallback={0}
					variant="brand"
					equalWidthOptions
					rootWidth="100%"
					textWrap="nowrap"
					fontSizeOverrides={SWITCH_FONT_SIZE}
				/>
				{/* Announced: switching mode changes what the same list of people is about to go through. */}
				<Typography role="status" aria-live="polite" variant="body2" sx={{ color: "text.secondary" }} data-testid="team-bulk-jobs-mode-hint">
					{picks.action === "add" ? t("team.bulk.jobs.add.hint") : t("team.bulk.jobs.replace.hint")}
				</Typography>
			</Box>
			{picks.action === "add" ? (
				<JobSelector
					variant="dropdown"
					singleSelect
					hideAllOption
					fullWidth
					jobs={enabledJobs}
					selectedJobIds={picks.addJobId ? [picks.addJobId] : []}
					onSelectedJobsChanged={(ids) => onPicksChange({ ...picks, addJobId: ids[0] })}
					label={t("team.bulk.jobs.field.add")}
					dataTestId="team-bulk-jobs-add-job"
				/>
			) : (
				<ReplacePickers picks={picks} onPicksChange={onPicksChange} enabledJobs={enabledJobs} heldJobs={heldJobs} />
			)}
		</Box>
	);
};

const ReplacePickers: FC<Props> = ({ picks, onPicksChange, enabledJobs, heldJobs }) => {
	const { t } = useTranslation();
	// The job being replaced can't also be its replacement.
	const replacementJobs = useMemo(() => enabledJobs.filter((job) => job.id !== picks.fromJobId), [enabledJobs, picks.fromJobId]);

	if (heldJobs.length === 0) {
		return (
			<InlineHint tone="neutral" dataTestId="team-bulk-jobs-no-held-jobs">
				{t("team.bulk.jobs.from.empty")}
			</InlineHint>
		);
	}

	const handleFromChange = (ids: string[]) => {
		const fromJobId = ids[0];
		onPicksChange({ ...picks, fromJobId, toJobId: picks.toJobId === fromJobId ? undefined : picks.toJobId });
	};

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto minmax(0, 1fr)" }, alignItems: "center", gap: 1.5 }}>
				<JobSelector
					variant="dropdown"
					singleSelect
					hideAllOption
					fullWidth
					jobs={heldJobs}
					selectedJobIds={picks.fromJobId ? [picks.fromJobId] : []}
					onSelectedJobsChanged={handleFromChange}
					label={t("team.bulk.jobs.field.from")}
					dataTestId="team-bulk-jobs-from-job"
				/>
				<ArrowForwardRounded
					aria-hidden
					sx={{
						color: "text.secondary",
						justifySelf: "center",
						transform: { xs: "rotate(90deg)", sm: "none" },
						"[dir='rtl'] &": { transform: { xs: "rotate(90deg)", sm: "scaleX(-1)" } },
					}}
				/>
				<JobSelector
					variant="dropdown"
					singleSelect
					hideAllOption
					fullWidth
					jobs={replacementJobs}
					selectedJobIds={picks.toJobId ? [picks.toJobId] : []}
					onSelectedJobsChanged={(ids) => onPicksChange({ ...picks, toJobId: ids[0] })}
					label={t("team.bulk.jobs.field.to")}
					dataTestId="team-bulk-jobs-to-job"
				/>
			</Box>
			<Typography variant="caption" sx={{ color: "text.secondary" }}>
				{t("team.bulk.jobs.from.helper")}
			</Typography>
		</Box>
	);
};
